import { useMemo } from "react";
import type { Airport, TripDetailsV2 } from "@b2bportal/air-shopping-api";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import { AirlineIcon } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import defaultStyles from "./FlightSummary.module.scss";

export interface FlightSummaryComponentProps extends PlatformComponentProps {
  tripDetails: TripDetailsV2;
  airports: { [key: string]: Airport };
  sliceIndex: number;
}

export const FlightSummary = ({
  tripDetails,
  airports,
  sliceIndex,
  className,
  children,
}: FlightSummaryComponentProps) => {
  const styles = useFlightStyles(
    CoreFlightsComponent.FlightSummary,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreFlightsComponent.FlightSummary);

  const { t, formatDateTime, DateTimeFormatStyle } = useI18nContext();

  const tripSlice = tripDetails.slices[sliceIndex];
  const firstTripSegment = tripSlice.segmentDetails[0];

  const flightDuration = useMemo(() => {
    const duration = dayjs.duration(tripSlice.totalDurationMinutes, "minutes");
    const units = [
      { value: duration.days(), key: "days" },
      { value: duration.hours(), key: "hours" },
      { value: duration.minutes(), key: "minutes" },
    ];

    const durationString = units
      .filter((unit) => unit.value)
      .map((unit) => t(`flightDuration.${unit.key}`, { count: unit.value }))
      .join(" ");

    return durationString.trim();
  }, [t, tripSlice.totalDurationMinutes]);

  const departure = sliceIndex === 0;
  return (
    <div className={clsx(block, className)}>
      <div className={cn("summary")}>
        <Trans
          i18nKey={
            departure
              ? "flightShopReview.departureCardHeader"
              : "flightShopReview.returnCardHeader"
          }
          values={{
            location: airports[tripSlice.destinationCode]
              ? airports[tripSlice.destinationCode].cityName
              : tripSlice.destinationName,
            date: formatDateTime(
              dayjs(removeTimezone(tripSlice.departureTime)).toDate(),
              DateTimeFormatStyle.ShortMonthDayShortWeekday
            ),
          }}
          components={{ strong: <span className={cn("summary-highlight")} /> }}
        />
      </div>

      <div className={cn("time")}>
        {`${formatDateTime(
          dayjs(removeTimezone(tripSlice.departureTime)).toDate(),
          DateTimeFormatStyle.ShortTime
        )} - ${formatDateTime(
          dayjs(removeTimezone(tripSlice.arrivalTime)).toDate(),
          DateTimeFormatStyle.ShortTime
        )}`}
      </div>

      <div className={cn("duration")}>{flightDuration}</div>
      <div className={cn("airline")}>
        <AirlineIcon
          airlineCode={firstTripSegment.airlineCode}
          className={cn("icon")}
          size="small"
        />
        <span>{firstTripSegment.airlineName}</span>
      </div>
      <div className={cn("stops")}>
        {tripSlice.stops === 0
          ? t("stopDetails.nonstop")
          : t("stopDetails.stop", { count: tripSlice.stops })}
      </div>
      {children}
    </div>
  );
};
